// ── KostenartRow — single row (root or child) in the Kostenarten table ────────
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, TextInput, Toggle } from '../../../../shared/ui'
import type { Kostenart } from '../api/kostenartenApi'

interface Props {
  row: Kostenart
  isChild: boolean
  isFirst: boolean
  isLast: boolean
  /** Whether this is the last row among its siblings (for ▲▼ bounds). */
  onMoveUp: () => void
  onMoveDown: () => void
  onToggleActive: () => void
  onNameSave: (name: string) => void
  isMutating: boolean
  /** Accordion (root rows only): number of child rows under this root. */
  childCount?: number
  /** Accordion: whether the children are currently shown. */
  expanded?: boolean
  /** Accordion: toggle handler — when set, the root marker becomes a chevron. */
  onToggleExpand?: () => void
}

export function KostenartRow({
  row,
  isChild,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onToggleActive,
  onNameSave,
  isMutating,
  childCount = 0,
  expanded = false,
  onToggleExpand,
}: Props) {
  const { t } = useTranslation('kostenarten')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(row.name)

  function startEdit() {
    setDraft(row.name)
    setEditing(true)
  }

  function commitEdit() {
    const trimmed = draft.trim()
    if (trimmed && trimmed !== row.name) {
      onNameSave(trimmed)
    }
    setEditing(false)
  }

  function cancelEdit() {
    setDraft(row.name)
    setEditing(false)
  }

  const inactive = !row.active

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group
        ${isChild ? 'ml-6 pl-4 border-l-2 border-outline-variant/30' : ''}
        ${inactive ? 'opacity-50' : 'hover:bg-surface-container-low/60'}
      `}
    >
      {/* Kind badge + code */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Root rows: accordion chevron (with child count when collapsed);
            falls back to the static indent marker when there are no children. */}
        {!isChild && (
          onToggleExpand && childCount > 0 ? (
            <button
              type="button"
              onClick={onToggleExpand}
              title={expanded ? t('accordion.collapse') : t('accordion.expand')}
              aria-expanded={expanded}
              className="flex items-center gap-1 flex-shrink-0 rounded-lg px-1 py-0.5 -ml-1 text-on-surface-variant/60 hover:text-on-surface hover:bg-surface-container-low transition-colors"
            >
              <span className="material-symbols-outlined !text-base transition-transform" style={{ transform: expanded ? 'rotate(90deg)' : undefined }}>
                chevron_right
              </span>
              {!expanded && (
                <span className="text-[10px] font-mono tabular-nums">{childCount}</span>
              )}
            </button>
          ) : (
            <span className="material-symbols-outlined !text-base text-on-surface-variant/50 flex-shrink-0">
              subdirectory_arrow_right
            </span>
          )
        )}

        {/* Inline name edit */}
        {editing ? (
          <div className="flex items-center gap-2 flex-1">
            <TextInput
              value={draft}
              onChange={e => setDraft(e.target.value)}
              size="compact"
              variant="surface"
              onKeyDown={e => {
                if (e.key === 'Enter') commitEdit()
                if (e.key === 'Escape') cancelEdit()
              }}
              onBlur={commitEdit}
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <span
              className={`text-sm font-medium truncate min-w-0
                ${inactive ? 'text-on-surface-variant' : 'text-on-surface'}
              `}
            >
              {row.name}
            </span>
            <span className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                icon="edit"
                onClick={startEdit}
                title={t('row.editName')}
                disabled={isMutating}
              />
            </span>
          </div>
        )}

        {/* Code pill — muted mono */}
        <span className="flex-shrink-0 font-mono text-[10px] bg-surface-container px-1.5 py-0.5 rounded text-on-surface-variant">
          {row.code}
        </span>

        {/* Kind badge */}
        <span
          className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full
            ${row.kind === 'cost'
              ? 'bg-rose-50 text-rose-600'
              : 'bg-emerald-50 text-emerald-700'
            }
          `}
        >
          {t(`kind.${row.kind}`)}
        </span>

        {/* Inactive badge */}
        {inactive && (
          <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-surface-container text-on-surface-variant">
            {t('status.inactive')}
          </span>
        )}
      </div>

      {/* ▲▼ order controls */}
      <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          icon="arrow_upward"
          onClick={onMoveUp}
          disabled={isFirst || isMutating}
          title={t('row.moveUp')}
        />
        <Button
          variant="ghost"
          size="sm"
          icon="arrow_downward"
          onClick={onMoveDown}
          disabled={isLast || isMutating}
          title={t('row.moveDown')}
        />
      </div>

      {/* Active toggle */}
      <div className="flex-shrink-0">
        <Toggle
          checked={row.active}
          onChange={onToggleActive}
          size="sm"
          disabled={isMutating}
        />
      </div>
    </div>
  )
}
